import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { useMainStore } from "./store/index";
import i18n from "./setup/i18n";
import vuetify from "./setup/vuetify";
import SocioVueComponents, {
  setTelemetryUser,
  initializeTracingAndLogging,
} from "@socotec.io/socio-vue-components";
import VueSignaturePad from "vue-signature-pad";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import wb from "./registerServiceWorker";

import SocioManifest from "../socio-manifest.json";
import PackageJson from "../package.json";
import { createPinia } from "pinia";
import { createORM } from "pinia-orm";
import { createOidc, useOidcStore } from "vue3-oidc";
import { oidcSettings } from "@/setup/OIDCSettings";
import * as Sentry from "@sentry/vue";
import { getDefaultSentryConfig } from "@socotec.io/socio-grpc-api/configs";
import { createSentryPiniaPlugin } from "@sentry/vue";

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

createOidc({
  oidcSettings: oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      useOidcStore().actions.value.setUser(user);
      setTelemetryUser(user, faroSettings.enableTelemetry);
    },
  },
  refreshToken: {
    enable: true,
    time: 30000,
  },
});

initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("fr");
dayjs.extend(duration);

const app = createApp(App);

Sentry.init(
  getDefaultSentryConfig({
    app,
    dsn: "https://3c05651c78601efa86c61d6f13541f17@sentry.tools.infra-socotec.net/17",
    router,
    enabled: import.meta.env.MODE !== "development",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
    release: `certipers@${import.meta.env.VITE_APP_VERSION}`,
  }),
);

const pinia = createPinia().use(createORM());
pinia.use(createSentryPiniaPlugin());

app.use(pinia);

app.config.globalProperties.$isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
app.config.globalProperties.$dayjs = dayjs;
app.config.globalProperties.$workbox = wb;

app
  .use(router)
  .use(i18n)
  .use(vuetify)
  .use(SocioVueComponents, i18n)
  .use(VueSignaturePad)
  .mount("#app");

const mainStore = useMainStore();
app.use(mainStore);
